import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { keys } from "./keys"
import { fetchPdsEntries, fetchPdsEntry, PdsAdd, PdsDelete, PdsUpdate } from "./pds.api";
import { seededDefinitionQueryOptions } from "./definition-queries";
import { getClientInformation } from "./client.api";
import { addApplication, getApplications, getDemographic } from "./applications.api";
import { getIncident, getIncidentDefinitions, getIncidents, incidentFollowupResponseUpsert, incidentFollowupStaffUpsert, incidentFollowupUpsert, incidentInitialUpsert } from "./incidents.api";
import { fetchScreening, upsertScreening } from "./screening.api";
import {
    applicationStatusAdd,
    applicationStatusUpdate,
    applicationStatusVoid,
    fetchApplicationStatus,
    fetchApplicationStatusDefinitions,
    fetchApplicationStatusHistory,
    fetchApplicationStatusTransitionSummary,
    getHistory
} from "./applicationstatus.api";
import { fetchQuickLinksConfig, saveUserQuickLinks } from "./quicklinks.api";
import {
    fetchManageGroupAppDefinitions,
    fetchManageGroupAppRole,
    updateManageGroupAppRole
} from "./managegroupapp.api";
import {
    deleteDocument,
    downloadDocument,
    fetchDocuments,
    uploadDocuments
} from "./documents.api";

export const usePdsQuery = (ApplicationID) => {
    return useQuery({
        queryKey: keys.pds.list(ApplicationID),
        queryFn: () => fetchPdsEntries(ApplicationID),
        placeholderData: (prev) => prev
    })
}

export const usePdsDefintionsQuery = () => {
    return useQuery(seededDefinitionQueryOptions("Applications/PDS/definitions"));
}

export const usePdsRecordQuery = (ApplicationID,PDSId,enabled = false) => {
    return useQuery({
        queryKey: keys.pds.detail(PDSId,ApplicationID),
        queryFn: () => fetchPdsEntry(ApplicationID,PDSId),
        placeholderData: (prev) => prev,
        enabled: enabled
    })
}

export const usePdsAdd = (ApplicationID,successCallback = null,errorCallback = null) => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: PdsAdd,
        onMutate: async(newItem) => {
            await qc.cancelQueries({queryKey: keys.pds.list(ApplicationID)});
            const prev = qc.getQueryData(keys.pds.list(ApplicationID));
            qc.setQueryData(keys.pds.list(ApplicationID), (old) => {
                if(!old) return old;

                const updated = [...old,{...newItem,lastModifiedDate: new Date().toISOString()}];

                return updated.sort((a,b) => new Date(b.startDate) - new Date(a.startDate)); //Optimistically set data
            });
            return {prev};
        },
        onError: (err,_vars,ctx) => {
            if(ctx?.prev) qc.setQueryData(keys.pds.list(ApplicationID),ctx.prev);
            if(errorCallback) errorCallback();
        },
        onSuccess: () => {
            if(successCallback) successCallback();
        },
        onSettled: () => {
            qc.invalidateQueries({queryKey: keys.pds.list(ApplicationID)});
        }
    })
}

export const usePdsUpdate = (ApplicationID,successCallback = null,errorCallback = null) => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: PdsUpdate,
        onMutate: async(updatedItem) => {
            await qc.cancelQueries({queryKey: keys.pds.list(ApplicationID)});
            const prev = qc.getQueryData(keys.pds.list(ApplicationID));
            qc.setQueryData(keys.pds.list(ApplicationID),(old) => {
                if(!old) return old;

                return old.map(item => item.id === updatedItem.id ? { ...item, ...updatedItem } : item);
            });

            return {prev};
        },
        onError: (_err,{id}, ctx) => { 
            if(ctx?.prev) qc.setQueryData(keys.pds.list(ApplicationID), ctx.prev);
            if(errorCallback) errorCallback();
        },
        onSuccess: (_data,{id},ctx) => {
            qc.invalidateQueries({queryKey: keys.pds.detail(id,ApplicationID)});
            if(successCallback) successCallback();
        },
        onSettled: (_data,_err,{id}) => {
            qc.invalidateQueries({queryKey: keys.pds.list(ApplicationID)});
        }
    });
}

export const usePdsDelete = (ApplicationID,successCallback = null,errorCallback = null) => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: PdsDelete,
        onMutate: async(deleteItem) => {
            await qc.cancelQueries({queryKey: keys.pds.list(ApplicationID)});
            const prev = qc.getQueryData(keys.pds.list(ApplicationID));
            qc.setQueryData(keys.pds.list(ApplicationID),(old) => {
                if(!old) return old;

                return old.map(item => item.id === deleteItem.PDSId ? { ...item, isDeleting: true } : item);
            });

            return {prev};
        },
        onError: (_err,{id}, ctx) => {
            if(errorCallback) errorCallback();
        },
        onSuccess: (_data,{id},ctx) => {
            if(successCallback) successCallback();
        },
        onSettled: () => {
            qc.invalidateQueries({queryKey: keys.pds.list(ApplicationID)});
        }
    });
}

export const useClientInformationQuery = (medicaidid) => {
    return useQuery({
        queryKey: keys.client.info(medicaidid),
        queryFn: () => getClientInformation(medicaidid),
        placeholderData: (prev) => prev
    })
}

export const useGetApplicationsQuery = (medicaidid) => {
    return useQuery({
        queryKey: keys.applications.list(medicaidid),
        queryFn:() => getApplications(medicaidid),
        placeholderData: (prev) => prev
    })
}

export const useGetDemographicsQuery = (applicationid) => {
    return useQuery({
        queryKey: keys.applications.demographics(applicationid),
        queryFn:() => getDemographic(applicationid),
        placeholderData: (prev) => prev,
        staleTime: 15_000
    })
} 

export const useApplicationsAdd = (medicaidid,successCallback,errorCallback) => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: addApplication,
        onSuccess: () => {
            qc.invalidateQueries({queryKey: keys.applications.list(medicaidid)})
            if(successCallback) successCallback();
        },
        onError: () => {
            if(errorCallback) errorCallback();
        }
    })
}

export const useGetIncidents = (medicaidid) => {
    return useQuery({
        queryKey: keys.incidents.list(medicaidid),
        queryFn: () => getIncidents(medicaidid),
        placeholderData: (prev) => prev,
        enabled: !!medicaidid
    })
}

export const useGetIncident = (id) => {
    return useQuery({
        queryKey: keys.incidents.detail(id),
        queryFn: () => getIncident(id),
        placeholderData: (prev) => prev,
        enabled: !!id
    })
}

export const useGetIncidentDefintions = () => {
    return useQuery({
        queryKey: keys.incidents.defintions(),
        queryFn: getIncidentDefinitions,
        placeholderData: (prev) => prev
    })
}

export const useIncidentInitialUpsert = (id,medicaidid,submitSuccesscallback,submitErrorCallback) => {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: incidentInitialUpsert,
        onSuccess: (data) => {
            qc.setQueryData(keys.incidents.detail(data.id),data);
            if(submitSuccesscallback) submitSuccesscallback(data);
        },
        onError: (error) => {
            if(submitErrorCallback) submitErrorCallback(error);
        },
        onSettled: () => {
            qc.invalidateQueries({queryKey: keys.incidents.detail(id)});
            qc.invalidateQueries({queryKey: keys.incidents.list(medicaidid)});
        }
    })
}

export const useIncidentFollowupUpsert = (id,medicaidid,submitSuccesscallback,submitErrorCallback) => {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: incidentFollowupUpsert,
        onSuccess: (data) => {
            qc.setQueryData(keys.incidents.detail(id),data);
            if(submitSuccesscallback) submitSuccesscallback(data);
        },
        onError: (error) => {
            if(submitErrorCallback) submitErrorCallback(error);
        },
        onSettled: () => {
            qc.invalidateQueries({queryKey: keys.incidents.detail(id)});
            qc.invalidateQueries({queryKey: keys.incidents.list(medicaidid)});
        }
    })
}

export const useIncidentFollowupStaffUpsert = (id,medicaidid,submitSuccesscallback,submitErrorCallback) => {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: incidentFollowupStaffUpsert,
        onSuccess: (data) => {
            qc.setQueryData(keys.incidents.detail(id),data);
            if(submitSuccesscallback) submitSuccesscallback(data);
        },
        onError: (error) => {
            if(submitErrorCallback) submitErrorCallback(error);
        },
        onSettled: () => {
            qc.invalidateQueries({queryKey: keys.incidents.detail(id)});
            qc.invalidateQueries({queryKey: keys.incidents.list(medicaidid)});
        }
    })
}

export const useIncidentFollowupResponseUpsert = (id,medicaidid,submitSuccesscallback,submitErrorCallback) => {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: incidentFollowupResponseUpsert,
        onSuccess: (data) => {
            qc.setQueryData(keys.incidents.detail(id),data);
            if(submitSuccesscallback) submitSuccesscallback(data);
        },
        onError: (error) => {
            if(submitErrorCallback) submitErrorCallback(error);
        },
        onSettled: () => {
            qc.invalidateQueries({queryKey: keys.incidents.detail(id)});
            qc.invalidateQueries({queryKey: keys.incidents.list(medicaidid)});
        }
    })
}

export const useGetScreening = (ApplicationID) => {
    return useQuery({
        queryKey: keys.screening.detail(ApplicationID),
        queryFn: () => fetchScreening(ApplicationID),
        placeholderData: (prev) => prev,
        enabled: !!ApplicationID
    })
}

export const useScreeningUpsert = (ApplicationID,successCallback = null,errorCallback = null) => {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: upsertScreening,
        onSuccess: (data) => {
            qc.setQueryData(keys.screening.detail(ApplicationID),data);
            if(successCallback) successCallback();
        },
        onError: () => {
            if(errorCallback) errorCallback();
        },
        onSettled: () => {
            qc.invalidateQueries({queryKey: keys.screening.detail(ApplicationID)});
        }
    })
}

const invalidateApplicationStatusQueries = (qc,ApplicationID) => {
    qc.invalidateQueries({queryKey: keys.applicationStatus.all()});
    qc.invalidateQueries({queryKey: keys.applicationHistory.list(ApplicationID)});
}

export const useApplicationStatusDefinitionsQuery = () => {
    return useQuery({
        queryKey: keys.applicationStatus.definitions(),
        queryFn: fetchApplicationStatusDefinitions,
        placeholderData: (prev) => prev
    })
}

export const useApplicationStatusHistoryQuery = (ApplicationID,ShowVoids = false,ShowInformationOnly = true) => {
    return useQuery({
        queryKey: keys.applicationStatus.list(ApplicationID,ShowVoids,ShowInformationOnly),
        queryFn: () => fetchApplicationStatusHistory(ApplicationID,ShowVoids,ShowInformationOnly),
        placeholderData: (prev) => prev,
        enabled: !!ApplicationID
    })
}

export const useApplicationStatusTransitionSummaryQuery = (ApplicationID) => {
    return useQuery({
        queryKey: keys.applicationStatus.transitionSummary(ApplicationID),
        queryFn: () => fetchApplicationStatusTransitionSummary(ApplicationID),
        placeholderData: (prev) => prev,
        enabled: !!ApplicationID
    })
}

export const useApplicationStatusRecordQuery = (ApplicationID,StatusID,enabled = false) => {
    return useQuery({
        queryKey: keys.applicationStatus.detail(ApplicationID,StatusID),
        queryFn: () => fetchApplicationStatus(ApplicationID,StatusID),
        placeholderData: (prev) => prev,
        enabled: !!ApplicationID && !!StatusID && enabled
    })
}

export const useApplicationStatusAdd = (ApplicationID,successCallback = null,errorCallback = null) => {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: applicationStatusAdd,
        onSuccess: (data) => {
            if(successCallback) successCallback(data);
        },
        onError: (error) => {
            if(errorCallback) errorCallback(error);
        },
        onSettled: () => {
            invalidateApplicationStatusQueries(qc,ApplicationID);
        }
    })
}

export const useApplicationStatusUpdate = (ApplicationID,successCallback = null,errorCallback = null) => {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: applicationStatusUpdate,
        onSuccess: (data,variables) => {
            qc.setQueryData(keys.applicationStatus.detail(ApplicationID,variables.HistoryID),data);
            if(successCallback) successCallback(data);
        },
        onError: (error) => {
            if(errorCallback) errorCallback(error);
        },
        onSettled: (_data,_error,variables) => {
            if(variables?.HistoryID){
                qc.invalidateQueries({queryKey: keys.applicationStatus.detail(ApplicationID,variables.HistoryID)});
            }
            invalidateApplicationStatusQueries(qc,ApplicationID);
        }
    })
}

export const useApplicationStatusVoid = (ApplicationID,ShowVoids = false,ShowInformationOnly = true,successCallback = null,errorCallback = null) => {
    const qc = useQueryClient();
    const listKey = keys.applicationStatus.list(ApplicationID,ShowVoids,ShowInformationOnly);

    return useMutation({
        mutationFn: applicationStatusVoid,
        onMutate: async(voidItem) => {
            await qc.cancelQueries({queryKey: listKey});
            const prev = qc.getQueryData(listKey);

            qc.setQueryData(listKey,(old) => {
                if(!old) return old;

                return old.map(item => item.id === voidItem.StatusID ? {...item,isvoid: voidItem.IsVoid} : item);
            });

            return {prev};
        },
        onError: (error,_vars,ctx) => {
            if(ctx?.prev) qc.setQueryData(listKey,ctx.prev);
            if(errorCallback) errorCallback(error);
        },
        onSuccess: (data) => {
            if(successCallback) successCallback(data);
        },
        onSettled: () => {
            invalidateApplicationStatusQueries(qc,ApplicationID);
        }
    })
}

export const useGetApplicationHistory = (ApplicationID) => {
    return useQuery({
        queryKey: keys.applicationHistory.list(ApplicationID),
        queryFn: () => getHistory(ApplicationID),
        placeholderData: (prev) => prev,
        enabled: !!ApplicationID
    })
}

export const useGetQuickLinks = (enabled = true) => {
    return useQuery({
        queryKey: keys.quickLinks.config(),
        queryFn: fetchQuickLinksConfig,
        placeholderData: (prev) => prev,
        staleTime: Infinity,
        gcTime: Infinity,
        enabled
    })
}

export const useQuickLinksUpdate = (successCallback = null,errorCallback = null) => {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: saveUserQuickLinks,
        onMutate: async(updatedConfig) => {
            await qc.cancelQueries({ queryKey: keys.quickLinks.config() });
            const prev = qc.getQueryData(keys.quickLinks.config());

            qc.setQueryData(keys.quickLinks.config(), updatedConfig);

            return { prev };
        },
        onError: (_err,_vars,ctx) => {
            if(ctx?.prev) qc.setQueryData(keys.quickLinks.config(), ctx.prev);
            if(errorCallback) errorCallback();
        },
        onSuccess: (data) => {
            qc.setQueryData(keys.quickLinks.config(), data);
            if(successCallback) successCallback(data);
        },
        onSettled: () => {
            qc.invalidateQueries({ queryKey: keys.quickLinks.config() });
        }
    })
}

export const useManageGroupAppDefinitionsQuery = () => {
    return useQuery({
        queryKey: keys.manageGroupApp.definitions(),
        queryFn: fetchManageGroupAppDefinitions,
        placeholderData: (prev) => prev
    })
}

export const useManageGroupAppRoleQuery = (RoleID) => {
    return useQuery({
        queryKey: keys.manageGroupApp.role(RoleID),
        queryFn: () => fetchManageGroupAppRole(RoleID),
        enabled: !!RoleID
    })
}

export const useManageGroupAppUpdate = (RoleID,successCallback = null,errorCallback = null) => {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: updateManageGroupAppRole,
        onSuccess: (data) => {
            qc.setQueryData(keys.manageGroupApp.role(RoleID), data);
            if(successCallback) successCallback(data);
        },
        onError: (error) => {
            if(errorCallback) errorCallback(error);
        },
        onSettled: () => {
            qc.invalidateQueries({ queryKey: keys.manageGroupApp.role(RoleID) });
            qc.invalidateQueries({ queryKey: keys.manageGroupApp.definitions() });
        }
    })
}

export const useDocumentsQuery = (ApplicationID) => {
    return useQuery({
        queryKey: keys.documents.list(ApplicationID),
        queryFn: () => fetchDocuments(ApplicationID),
        placeholderData: (prev) => prev,
        enabled: !!ApplicationID
    })
}

export const useUploadDocuments = (ApplicationID,successCallback = null,errorCallback = null) => {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: uploadDocuments,
        onSuccess: (data) => {
            if(successCallback) successCallback(data);
        },
        onError: (error) => {
            if(errorCallback) errorCallback(error);
        },
        onSettled: () => {
            qc.invalidateQueries({ queryKey: keys.documents.list(ApplicationID) });
        }
    })
}

export const useDeleteDocument = (ApplicationID,successCallback = null,errorCallback = null) => {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: deleteDocument,
        onSuccess: (data) => {
            if(successCallback) successCallback(data);
        },
        onError: (error) => {
            if(errorCallback) errorCallback(error);
        },
        onSettled: () => {
            qc.invalidateQueries({ queryKey: keys.documents.list(ApplicationID) });
        }
    })
}

export const useDownloadDocument = (successCallback = null,errorCallback = null) => {
    return useMutation({
        mutationFn: downloadDocument,
        onSuccess: (data,variables) => {
            if(successCallback) successCallback(data,variables);
        },
        onError: (error,variables) => {
            if(errorCallback) errorCallback(error,variables);
        }
    })
}
